import { useEffect, useCallback, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/button';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageLightboxProps {
  images: string[];
  selectedIndex: number;
  open: boolean;
  onClose: () => void;
  onChangeIndex: (index: number) => void;
  productName?: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.5;

export function ImageLightbox({
  images,
  selectedIndex,
  open,
  onClose,
  onChangeIndex,
  productName = 'Product',
}: ImageLightboxProps) {
  // Zoom state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);

  // Touch handling refs
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const lastTapTime = useRef<number>(0);
  const initialPinchDistance = useRef<number | null>(null);
  const initialScale = useRef<number>(1);

  // Pan/drag refs
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const lastPosition = useRef({ x: 0, y: 0 });

  const isZoomed = scale > 1;

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset zoom when changing image
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [selectedIndex]);

  // Reset zoom when closing
  useEffect(() => {
    if (!open) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [open]);

  const handlePrevious = useCallback(() => {
    if (isZoomed) return;
    const newIndex = selectedIndex === 0 ? images.length - 1 : selectedIndex - 1;
    onChangeIndex(newIndex);
  }, [selectedIndex, images.length, onChangeIndex, isZoomed]);

  const handleNext = useCallback(() => {
    if (isZoomed) return;
    const newIndex = selectedIndex === images.length - 1 ? 0 : selectedIndex + 1;
    onChangeIndex(newIndex);
  }, [selectedIndex, images.length, onChangeIndex, isZoomed]);

  const handleZoomIn = useCallback(() => {
    setScale((s) => Math.min(s + ZOOM_STEP, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((s) => {
      const newScale = Math.max(s - ZOOM_STEP, MIN_SCALE);
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          if (isZoomed) {
            handleResetZoom();
          } else {
            onClose();
          }
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handlePrevious, handleNext, onClose, handleZoomIn, handleZoomOut, handleResetZoom, isZoomed]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setScale((s) => {
      const newScale = Math.max(MIN_SCALE, Math.min(s + delta, MAX_SCALE));
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  }, []);

  // Get distance between two touch points
  const getTouchDistance = (touches: React.TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      initialPinchDistance.current = getTouchDistance(e.touches);
      initialScale.current = scale;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapTime.current < 300) {
        if (isZoomed) {
          handleResetZoom();
        } else {
          setScale(2);
        }
        lastTapTime.current = 0;
        return;
      }
      lastTapTime.current = now;

      if (isZoomed) {
        isDragging.current = true;
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        lastPosition.current = { ...position };
      } else {
        touchStartX.current = e.touches[0].clientX;
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance.current !== null) {
      const currentDistance = getTouchDistance(e.touches);
      const ratio = currentDistance / initialPinchDistance.current;
      const newScale = Math.max(MIN_SCALE, Math.min(initialScale.current * ratio, MAX_SCALE));
      setScale(newScale);
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1) {
      if (isZoomed && isDragging.current) {
        const dx = e.touches[0].clientX - dragStart.current.x;
        const dy = e.touches[0].clientY - dragStart.current.y;
        setPosition({
          x: lastPosition.current.x + dx,
          y: lastPosition.current.y + dy,
        });
      } else if (!isZoomed) {
        touchEndX.current = e.touches[0].clientX;
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isZoomed && touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      if (Math.abs(diff) > 50) {
        if (diff > 0) handleNext();
        else handlePrevious();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
    initialPinchDistance.current = null;
    isDragging.current = false;
  };

  // Mouse drag for panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isZoomed) {
      isDragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      lastPosition.current = { ...position };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isZoomed && isDragging.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPosition({
        x: lastPosition.current.x + dx,
        y: lastPosition.current.y + dy,
      });
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleDoubleClick = () => {
    if (isZoomed) handleResetZoom();
    else setScale(2);
  };

  if (images.length === 0) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] bg-black/95 border-none p-0 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            isMobile ? "w-full h-full max-w-full max-h-full rounded-none" : "max-w-[95vw] max-h-[95vh] rounded-lg"
          )}
        >
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className={cn(
            "relative flex items-center justify-center w-full overflow-hidden select-none",
            isMobile ? "h-full" : "min-h-[80vh]",
            isZoomed ? "cursor-grab active:cursor-grabbing" : ""
          )}
          style={{ touchAction: 'none' }}
        >
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white h-11 w-11"
          >
            <X className="h-5 w-5" />
          </Button>

          {/* Zoom controls */}
          <div className="absolute top-4 left-4 z-10 flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomOut}
              disabled={scale <= MIN_SCALE}
              className="bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 h-11 w-11"
            >
              <ZoomOut className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomIn}
              disabled={scale >= MAX_SCALE}
              className="bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 h-11 w-11"
            >
              <ZoomIn className="h-5 w-5" />
            </Button>
          </div>

          {/* Zoom level */}
          {isZoomed && (
            <div className="absolute top-16 left-4 z-10 bg-black/50 text-white text-xs px-2 py-1 rounded">
              {Math.round(scale * 100)}%
            </div>
          )}

          {/* Navigation buttons */}
          {images.length > 1 && !isMobile && !isZoomed && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevious}
                className="absolute left-4 bg-white/10 hover:bg-white/20 text-white h-11 w-11"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                className="absolute right-4 bg-white/10 hover:bg-white/20 text-white h-11 w-11"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}

          {/* Main image */}
          <img
            src={images[selectedIndex]}
            alt={`${productName} - Image ${selectedIndex + 1}`}
            onDoubleClick={handleDoubleClick}
            draggable={false}
            className="max-w-full max-h-[85vh] object-contain transition-transform duration-200"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              transition: isDragging.current ? 'none' : 'transform 0.2s ease-out',
            }}
          />

          {/* Image counter */}
          {images.length > 1 && (
            <div className={cn(
              "absolute left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full",
              isMobile ? "top-5" : "bottom-4"
            )}>
              {selectedIndex + 1} / {images.length}
            </div>
          )}

          {/* Mobile hints */}
          {isMobile && !isZoomed && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 text-white/60 text-xs text-center">
              {images.length > 1 && (
                <div className="flex items-center gap-1 justify-center mb-1">
                  <ChevronLeft className="h-4 w-4" />
                  Swipe
                  <ChevronRight className="h-4 w-4" />
                </div>
              )}
              <div>Double-tap pour zoomer</div>
            </div>
          )}

          {isMobile && isZoomed && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 text-white/60 text-xs">
              Double-tap pour réinitialiser
            </div>
          )}

          {/* Thumbnails (desktop only) */}
          {images.length > 1 && !isMobile && !isZoomed && (
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80%] overflow-x-auto py-2 px-4">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => onChangeIndex(index)}
                  className={cn(
                    "w-12 h-12 flex-shrink-0 rounded overflow-hidden border-2 transition-all",
                    index === selectedIndex
                      ? "border-primary opacity-100"
                      : "border-transparent opacity-60 hover:opacity-100"
                  )}
                >
                  <img
                    src={img}
                    alt={`${productName} thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Dot indicators (mobile only) */}
          {images.length > 1 && isMobile && !isZoomed && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => onChangeIndex(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    index === selectedIndex ? "bg-primary" : "bg-white/40"
                  )}
                />
              ))}
            </div>
          )}
        </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
